import type { ArabicSky, FigurePoint } from '../data/arabicSky';
import { STATION_WIDTH, fromEcliptic } from '../data/arabicSky';
import type { ConstellationData, DeepSky, StarCatalog } from '../data/catalog';
import { Vec3, altAzToEnu, norm, raDecToVec } from '../lib/math';
import { subdividePolyline, subdivideSegment } from './layers/lines';

export interface ConstellationGeometry {
  segments: Float32Array;
  gaps: Float32Array;
  /** Segment ranges per constellation (index into constellations list). */
  ranges: { first: number; count: number }[];
  /** Label anchor (J2000 unit vector) and star indices per constellation. */
  anchors: Vec3[];
  stars: number[][];
}

/** Snap a J2000 position to the nearest catalogue star (≤ 0.25°) so lines meet the stars exactly. */
function snapToStar(cat: StarCatalog, v: Vec3): { dir: Vec3; mag: number; index: number } {
  let best = -1, bestDot = Math.cos((0.25 * Math.PI) / 180);
  const d = cat.dir;
  // Constellation figures only use stars brighter than ~6.5; the catalogue is sorted by magnitude.
  for (let i = 0; i < cat.count && cat.mag[i] <= 6.8; i++) {
    const dot = d[i * 3] * v[0] + d[i * 3 + 1] * v[1] + d[i * 3 + 2] * v[2];
    if (dot > bestDot) {
      bestDot = dot;
      best = i;
    }
  }
  if (best < 0) return { dir: v, mag: 5, index: -1 };
  return { dir: [d[best * 3], d[best * 3 + 1], d[best * 3 + 2]], mag: cat.mag[best], index: best };
}

/** Gap around a star, in units of the line gap scale (≈ device px at scale 1). */
const gapFor = (mag: number) => 2.5 + Math.max(0, 4.5 - mag) * 1.1;

export function buildConstellations(data: ConstellationData, cat: StarCatalog): ConstellationGeometry {
  const seg: number[] = [];
  const gaps: number[] = [];
  const ranges: { first: number; count: number }[] = [];
  const anchors: Vec3[] = [];
  const stars: number[][] = [];
  for (const c of data.constellations) {
    const first = seg.length / 6;
    const members = new Set<number>();
    for (const line of c.lines) {
      const pts = line.map(([ra, dec]) => snapToStar(cat, raDecToVec(ra, dec)));
      for (const p of pts) if (p.index >= 0) members.add(p.index);
      for (let k = 0; k + 1 < pts.length; k++) {
        subdivideSegment(pts[k].dir, pts[k + 1].dir, 2.5, seg, gaps, gapFor(pts[k].mag), gapFor(pts[k + 1].mag));
      }
    }
    ranges.push({ first, count: seg.length / 6 - first });
    anchors.push(raDecToVec(c.label[0], c.label[1]));
    stars.push([...members]);
  }
  return { segments: new Float32Array(seg), gaps: new Float32Array(gaps), ranges, anchors, stars };
}

/**
 * Figures from other sky cultures. Points may be Hipparcos stars, outline points given as [RA hours,
 * Dec degrees] (for star fields and regions), or deep-sky objects ("DSO:M45"). A point that cannot be
 * found breaks the line rather than joining across it. Anchors are the centroids of all points.
 */
export function buildFigures(figures: { lines: FigurePoint[][] }[], cat: StarCatalog, dsos: DeepSky[], extra?: StarCatalog | null): ConstellationGeometry {
  const seg: number[] = [];
  const gaps: number[] = [];
  const ranges: { first: number; count: number }[] = [];
  const anchors: Vec3[] = [];
  const stars: number[][] = [];
  const d = cat.dir;
  // By Messier number or catalogue name ("M44" or "NGC2632").
  const dsoById = new Map(dsos.flatMap((o) => [[o.id.replace(/\s+/g, ''), o] as const, ...(o.cat ? [[o.cat.replace(/\s+/g, ''), o] as const] : [])]));
  const resolve = (p: FigurePoint): { dir: Vec3; gap: number; star: number } | null => {
    if (typeof p === 'number') {
      // Fainter figure stars may only be in the Tycho-2 file: placed, but not listed as members.
      const j = cat.byHip.has(p) ? undefined : extra?.byHip.get(p);
      if (j !== undefined && extra) return { dir: [extra.dir[j * 3], extra.dir[j * 3 + 1], extra.dir[j * 3 + 2]], gap: gapFor(extra.mag[j]), star: -1 };
      const i = cat.byHip.get(p);
      return i === undefined ? null : { dir: [d[i * 3], d[i * 3 + 1], d[i * 3 + 2]], gap: gapFor(cat.mag[i]), star: i };
    }
    if (Array.isArray(p)) return { dir: raDecToVec(p[0] * 15, p[1]), gap: 0, star: -1 };
    if (typeof p === 'string' && p.startsWith('DSO:')) {
      const o = dsoById.get(p.slice(4).replace(/\s+/g, ''));
      return o ? { dir: o.dir, gap: 4, star: -1 } : null;
    }
    return null;
  };
  for (const f of figures) {
    const first = seg.length / 6;
    const members = new Set<number>();
    const c: Vec3 = [0, 0, 0];
    let n = 0;
    for (const line of f.lines) {
      let prev: { dir: Vec3; gap: number; star: number } | null = null;
      for (const p of line) {
        const cur = resolve(p);
        if (!cur) {
          prev = null;
          continue;
        }
        if (cur.star >= 0) members.add(cur.star);
        for (let a = 0; a < 3; a++) c[a] += cur.dir[a];
        n++;
        if (prev) subdivideSegment(prev.dir, cur.dir, 2.5, seg, gaps, prev.gap, cur.gap);
        prev = cur;
      }
    }
    ranges.push({ first, count: seg.length / 6 - first });
    anchors.push(n ? norm(c) : [0, 0, 1]);
    stars.push([...members]);
  }
  return { segments: new Float32Array(seg), gaps: new Float32Array(gaps), ranges, anchors, stars };
}

/** Lunar station boundaries and their north and south limits. */
export function buildLunarStations(sky: ArabicSky, zeroLon: number): Float32Array {
  const seg: number[] = [];
  const ext = sky.lunar.extent;
  for (let p = 0; p < 28; p++) {
    const q = (p + 27) % 28;
    const lon0 = zeroLon + p * STATION_WIDTH, lon1 = lon0 + STATION_WIDTH;
    const n = Math.max(ext[2 * p], ext[2 * q]), s = Math.min(ext[2 * p + 1], ext[2 * q + 1]);
    const edge: Vec3[] = [];
    for (let b = s; b <= n + 1e-9; b += 1) edge.push(fromEcliptic(lon0, b));
    subdividePolyline(edge, 2, seg);
    for (const b of [ext[2 * p], ext[2 * p + 1]]) {
      const arc: Vec3[] = [];
      for (let t = 0; t <= 8; t++) arc.push(fromEcliptic(lon0 + (t / 8) * (lon1 - lon0), b));
      subdividePolyline(arc, 2, seg);
    }
  }
  return new Float32Array(seg);
}

export function buildBorders(data: ConstellationData): Float32Array {
  const seg: number[] = [];
  for (const line of data.borders) subdividePolyline(line.map(([ra, dec]) => raDecToVec(ra, dec)), 2, seg);
  return new Float32Array(seg);
}

/** Altitude/azimuth grid in ENU (also used for equatorial grids by passing a frame matrix). */
export function buildSphericalGrid(latStep: number, lonStep: number): Float32Array {
  const seg: number[] = [];
  for (let lat = -90 + latStep; lat < 90; lat += latStep) {
    const pts: Vec3[] = [];
    for (let lon = 0; lon <= 360; lon += 2) pts.push(altAzToEnu(lat, lon));
    subdividePolyline(pts, 2, seg);
  }
  for (let lon = 0; lon < 360; lon += lonStep) {
    const pts: Vec3[] = [];
    for (let lat = -88; lat <= 88; lat += 2) pts.push(altAzToEnu(lat, lon));
    subdividePolyline(pts, 2, seg);
  }
  return new Float32Array(seg);
}

/** A great circle given its pole (unit vector). */
export function buildGreatCircle(pole: Vec3): Float32Array {
  const [px, py, pz] = pole;
  // Two orthonormal vectors in the plane.
  let ax: Vec3 = Math.abs(pz) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  const d = ax[0] * px + ax[1] * py + ax[2] * pz;
  ax = [ax[0] - d * px, ax[1] - d * py, ax[2] - d * pz];
  const l = Math.hypot(...ax);
  ax = [ax[0] / l, ax[1] / l, ax[2] / l];
  const bx: Vec3 = [py * ax[2] - pz * ax[1], pz * ax[0] - px * ax[2], px * ax[1] - py * ax[0]];
  const pts: Vec3[] = [];
  for (let t = 0; t <= 360; t += 2) {
    const c = Math.cos((t * Math.PI) / 180), s = Math.sin((t * Math.PI) / 180);
    pts.push([ax[0] * c + bx[0] * s, ax[1] * c + bx[1] * s, ax[2] * c + bx[2] * s]);
  }
  const seg: number[] = [];
  subdividePolyline(pts, 2, seg);
  return new Float32Array(seg);
}

/** North pole of the J2000 mean ecliptic, in J2000 equatorial coordinates. */
export const ECLIPTIC_POLE: Vec3 = (() => {
  const eps = (23.4392911 * Math.PI) / 180;
  return [0, -Math.sin(eps), Math.cos(eps)];
})();
